import moment from 'moment';
import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import { createSelector } from 'reselect';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import { resetUploadedFilesAction } from '../../../features/files/ducks';
import { hideModal } from '../../../features/modal/ducks';
import loanApi from '../../../features/loan-management/api';
import allocationApi from '../../../features/allocation-management/api';
import { accountRoles, accountRegistrationStatuses } from '../../../common/enums';
import authApi from '../../../features/auth/api';
import { loginActions } from '../../../features/auth/ducks';
import accountApi, { apiCallIds as accountApiCallIds } from '../api';

/**
 * ACTION TYPES
 */
export const FETCH_ACCOUNTS = 'accountManagement/FETCH_ACCOUNTS';
export const FETCH_ACCOUNT = 'accountManagement/FETCH_ACCOUNT';
export const IMPERSONATE_USER = 'accountManagement/IMPERSONATE_USER';
export const FETCH_ACCOUNT_DOCUMENTS = 'accountManagement/FETCH_ACCOUNT_DOCUMENTS';
export const UPDATE_ACCOUNT_DOCUMENTS = 'accountManagement/UPDATE_ACCOUNT_DOCUMENTS';
export const ACCEPT_ACCOUNT_DOCUMENTS = 'accountManagement/ACCEPT_ACCOUNT_DOCUMENTS';
export const REJECT_ACCOUNT_DOCUMENTS = 'accountManagement/REJECT_ACCOUNT_DOCUMENTS';
export const SET_ACCOUNT_DOCUMENT_TYPE = 'accountManagement/SET_ACCOUNT_DOCUMENT_TYPE';
export const REMOVE_ACCOUNT_DOCUMENT = 'accountManagement/REMOVE_ACCOUNT_DOCUMENT';
export const FETCH_ACCOUNT_CONTRACTS = 'accountManagement/FETCH_ACCOUNT_CONTRACTS';
export const UPDATE_ACCOUNT = 'accountManagement/UPDATE_ACCOUNT';
export const UPDATE_ACCOUNT_PROFILE = 'accountManagement/UPDATE_ACCOUNT_PROFILE';
export const SET_ACCOUNTS_PAGINATION = 'accountManagement/SET_ACCOUNTS_PAGINATION';
export const SET_DETAIL_EDITABLE = 'accountManagement/SET_DETAIL_EDITABLE';
export const SET_PROFILE_EDITABLE = 'accountManagement/SET_PROFILE_EDITABLE';
export const SET_ACCOUNTS_FILTER = 'accountManagement/SET_ACCOUNTS_FILTER';
export const FETCH_ACCOUNT_LOANS = 'accountManagement/FETCH_ACCOUNT_LOANS';
export const FETCH_ACCOUNT_ACTIVE_LOANS = 'accountManagement/FETCH_ACCOUNT_ACTIVE_LOANS';
export const SET_ACCOUNT_LOANS_PAGINATION = 'accountManagement/SET_ACCOUNT_LOANS_PAGINATION';
export const SET_ACCOUNT_LOANS_FILTER = 'accountManagement/SET_ACCOUNT_LOANS_FILTER';
export const FETCH_ACCOUNT_ALLOCATIONS = 'allocationManagement/FETCH_ACCOUNT_ALLOCATIONS';
export const SET_ACCOUNT_ALLOCATIONS_PAGINATION =
  'allocationManagement/SET_ACCOUNT_ALLOCATIONS_PAGINATION';
export const SET_ACCOUNT_ALLOCATIONS_FILTER = 'allocationManagement/SET_ACCOUNT_ALLOCATIONS_FILTER';
export const CREATE_ADMINISTRATOR = 'accountManagement/CREATE_ADMINISTRATOR';
export const FETCH_ACCOUNT_MESSAGES = 'accountManagement/FETCH_ACCOUNT_MESSAGES';
export const FETCH_ACCOUNT_ALL_MESSAGES = 'accountManagement/FETCH_ACCOUNT_ALL_MESSAGES';
export const CREATE_ACCOUNT_MESSAGE = 'accountManagement/CREATE_ACCOUNT_MESSAGE';
export const FETCH_ID_CARD_INFO = 'accountManagement/FETCH_ID_CARD_INFO';
export const SET_ACCOUNT_MESSAGES_FILTER = 'allocationManagement/SET_ACCOUNT_MESSAGES_FILTER';
export const FETCH_ACCOUNT_UPDATE_LOGS = 'accountManagement/FETCH_ACCOUNT_UPDATE_LOGS';
export const FETCH_FEE_ACCOUNT_AMOUNT = 'accountManagement/FETCH_FEE_ACCOUNT_AMOUNT';
export const CHECK_GENERAL_CONTRACT_DATE = 'accountManageent/CHECK_GENERAL_CONTRACT_DATE';


/**
 * ACTIONS
 */
export const fetchAccountsActions = createApiActionCreators(FETCH_ACCOUNTS);
export const fetchAccountActions = createApiActionCreators(FETCH_ACCOUNT);
export const impersonateUserActions = createApiActionCreators(IMPERSONATE_USER);
export const fetchAccountDocumentsActions = createApiActionCreators(FETCH_ACCOUNT_DOCUMENTS);
export const updateAccountDocumentsActions = createApiActionCreators(UPDATE_ACCOUNT_DOCUMENTS);
export const acceptAccountDocumentsActions = createApiActionCreators(ACCEPT_ACCOUNT_DOCUMENTS);
export const rejectAccountDocumentsActions = createApiActionCreators(REJECT_ACCOUNT_DOCUMENTS);
export const setAccountDocumentTypeAction = createActionCreator(SET_ACCOUNT_DOCUMENT_TYPE);
export const removeAccountDocumentAction = createActionCreator(REMOVE_ACCOUNT_DOCUMENT);
export const fetchAccountContractsActions = createApiActionCreators(FETCH_ACCOUNT_CONTRACTS);
export const updateAccountActions = createApiActionCreators(UPDATE_ACCOUNT);
export const updateAccountProfileActions = createApiActionCreators(UPDATE_ACCOUNT_PROFILE);
export const fetchAccountLoansActions = createApiActionCreators(FETCH_ACCOUNT_LOANS);
export const fetchAccountActiveLoansActions = createApiActionCreators(FETCH_ACCOUNT_ACTIVE_LOANS);
export const fetchAccountAllocationsActions = createApiActionCreators(FETCH_ACCOUNT_ALLOCATIONS);
export const createAdministratorActions = createApiActionCreators(CREATE_ADMINISTRATOR);
export const fetchAccountMessagesActions = createApiActionCreators(FETCH_ACCOUNT_MESSAGES);
export const fetchAccountAllMessagesActions = createApiActionCreators(FETCH_ACCOUNT_ALL_MESSAGES);
export const createAccountMessageActions = createApiActionCreators(CREATE_ACCOUNT_MESSAGE);
export const fetchIdCardInfoActions = createApiActionCreators(FETCH_ID_CARD_INFO);
export const fetchAccountUpdateLogsActions = createApiActionCreators(FETCH_ACCOUNT_UPDATE_LOGS);


export const setAccountsPaginationAction = createActionCreator(SET_ACCOUNTS_PAGINATION);
export const setDetailEditableAction = createActionCreator(SET_DETAIL_EDITABLE);
export const setProfileEditableAction = createActionCreator(SET_PROFILE_EDITABLE);
export const setAccountsFilterAction = createActionCreator(SET_ACCOUNTS_FILTER);
export const setAccountLoansPaginationAction = createActionCreator(SET_ACCOUNT_LOANS_PAGINATION);
export const setAccountLoansFilterAction = createActionCreator(SET_ACCOUNT_LOANS_FILTER);
export const setAccountAllocationsPaginationAction = createActionCreator(
  SET_ACCOUNT_ALLOCATIONS_PAGINATION
);
export const setAccountAllocationsFilterAction = createActionCreator(
  SET_ACCOUNT_ALLOCATIONS_FILTER
);
export const setAccountMessagesFilterAction = createActionCreator(SET_ACCOUNT_MESSAGES_FILTER);
export const fetchSystemFeeAccountAmountActions = createApiActionCreators(FETCH_FEE_ACCOUNT_AMOUNT);
export const checkGeneralContractDateActions = createApiActionCreators(CHECK_GENERAL_CONTRACT_DATE);

/**
 * REDUCERS
 */
const initialState = {
  account: null,
  documents: {
    items: [],
    version: 0,
  },
  contracts: {
    items: [],
    version: 0,
  },
  loans: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      status: '',
    },
  },
  activeLoans: {
    items: [],
  },
  allocations: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      status: '',
    },
  },
  accounts: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      search: '',
      role: [accountRoles.CREDITOR, accountRoles.DEBTOR],
      registrationStatus: accountRegistrationStatuses.DOCUMENTS_SUBMITTED,
      locked: false,
    },
  },
  messages: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
  },
  allMessages: null,
  messagesFilter: ['internal', 'external'],
  detailEditable: '',
  profileEditable: '',
  checkGeneralContractDate: null,
  updateLogs: {
    items: []
  },
  feeAccountAmount: 0
};

const account = createReducer(initialState.account, {
  [FETCH_ACCOUNT]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: state => null,
  },
  [UPDATE_ACCOUNT]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [UPDATE_ACCOUNT_PROFILE]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [CREATE_ADMINISTRATOR]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [ACCEPT_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [REJECT_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [FETCH_ID_CARD_INFO]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      idCardInfo: payload,
    })
  },
  // [CHECK_GENERAL_CONTRACT_DATE]: {
  //   [SUCCESS]: (state, payload) => payload,
  //   [FAILURE]: state => null
  // }
});

const documents = createReducer(initialState.documents, {
  [FETCH_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.documents,
      version: payload.version,
    }),
  },
  [UPDATE_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.documents,
      version: payload.version,
    }),
  },
  [SET_ACCOUNT_DOCUMENT_TYPE]: (state, { id, type }) => ({
    ...state,
    items: state.items.map(document => {
      if (document.file.id === id) {
        document.type = type;
      }
      return document;
    }),
  }),
  [REMOVE_ACCOUNT_DOCUMENT]: (state, payload) => ({
    ...state,
    items: state.items.filter(document => document.file.id !== payload),
  }),
});

const contracts = createReducer(initialState.contracts, {
  [FETCH_ACCOUNT_CONTRACTS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload,
      version: payload.version,
    }),
  },
});

const loans = createReducer(initialState.loans, {
  [FETCH_ACCOUNT_LOANS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_ACCOUNT_LOANS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_ACCOUNT_LOANS_FILTER]: (state, payload) => ({
    ...state,
    filter: {
      status: payload.status,
    },
  }),
});

const activeLoans = createReducer(initialState.activeLoans, {
  [FETCH_ACCOUNT_ACTIVE_LOANS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
});

const allocations = createReducer(initialState.allocations, {
  [FETCH_ACCOUNT_ALLOCATIONS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_ACCOUNT_ALLOCATIONS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_ACCOUNT_ALLOCATIONS_FILTER]: (state, payload) => ({
    ...state,
    filter: {
      status: payload.status,
    },
  }),
});

const accounts = createReducer(initialState.accounts, {
  [FETCH_ACCOUNTS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_ACCOUNTS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_ACCOUNTS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
});

const messages = createReducer(initialState.messages, {
  [FETCH_ACCOUNT_MESSAGES]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [CREATE_ACCOUNT_MESSAGE]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: [
        ...(
          ((payload.messagesFilter.includes('internal') && !payload.data.isExternal) ||
            (payload.messagesFilter.includes('external') && payload.data.isExternal) ||
            payload.messagesFilter.length === 0) ? [payload.data] : []),
        ...state.items,
      ],
    }),
  },
});

const allMessages = createReducer(initialState.allMessages, {
  [FETCH_ACCOUNT_ALL_MESSAGES]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: state => null,
  },
});

const messagesFilter = createReducer(initialState.messagesFilter, {
  [SET_ACCOUNT_MESSAGES_FILTER]: (state, payload) => payload.newFilter,
});

const detailEditable = createReducer(initialState.detailEditable, {
  [SET_DETAIL_EDITABLE]: (state, payload) => payload,
});

const profileEditable = createReducer(initialState.profileEditable, {
  [SET_PROFILE_EDITABLE]: (state, payload) => payload,
});

const updateLogs = createReducer(initialState.updateLogs, {
  [FETCH_ACCOUNT_UPDATE_LOGS]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: state => null,
  },
});

const feeAccountAmount = createReducer(initialState.feeAccountAmount, {
  [FETCH_FEE_ACCOUNT_AMOUNT]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => 0
  }
})

const checkGeneralContractDate = createReducer(initialState.checkGeneralContractDate, {
  [CHECK_GENERAL_CONTRACT_DATE]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: state => null
  }
}) 

export default combineReducers({
  account,
  documents,
  contracts,
  loans,
  activeLoans,
  allocations,
  accounts,
  detailEditable,
  profileEditable,
  messages,
  allMessages,
  messagesFilter,
  updateLogs,
  feeAccountAmount,
  checkGeneralContractDate
});

/**
 * SELECTORS
 */
export const selectAccountState = state => state.account;
export const selectAccount = state => selectAccountState(state).account;

export const selectDocumentsState = state => selectAccountState(state).documents;
export const selectDocuments = state => selectDocumentsState(state).items;
export const selectDocumentsVersion = state => selectDocumentsState(state).version;

export const selectContractsState = state => selectAccountState(state).contracts;
export const selectContracts = state => selectContractsState(state).items;

export const selectLoansState = state => selectAccountState(state).loans;
export const selectLoans = state => selectLoansState(state).items;
export const selectLoansPagination = state => selectLoansState(state).pagination;
export const selectLoansFilter = state => selectLoansState(state).filter;
export const selectActiveLoansState = state => selectAccountState(state).activeLoans;
export const selectActiveLoans = state => selectActiveLoansState(state).items;
export const selectLoansCount = state => selectLoansPagination(state).total;

export const selectAllocationsState = state => selectAccountState(state).allocations;
export const selectAllocations = state => selectAllocationsState(state).items;
export const selectAllocationsPagination = state => selectAllocationsState(state).pagination;
export const selectAllocationsFilter = state => selectAllocationsState(state).filter;

export const selectAccountsState = state => selectAccountState(state).accounts;
export const selectAccounts = state => selectAccountsState(state).items;
export const selectAccountsPagination = state => selectAccountsState(state).pagination;
export const selectAccountsFilter = state => selectAccountsState(state).filter;

export const selectDetailEditable = state => selectAccountState(state).detailEditable;
export const selectProfileEditable = state => selectAccountState(state).profileEditable;

export const selectMessagesState = state => selectAccountState(state).messages;
export const selectMessages = state => selectMessagesState(state).items;
export const selectAllMessages = state => selectAccountState(state).allMessages;

export const selectMessagesFilter = state => selectAccountState(state).messagesFilter;

export const selectAccountUpdateLogs = state => selectAccountState(state).updateLogs;

export const selectFeeAccountAmount = state => selectAccountState(state).feeAccountAmount
export const selectCheckGeneralContract = state => selectAccountState(state).checkGeneralContractDate

export const selectAccountFullName = createSelector(
  selectAccount,
  account => (account ? `${account.nameFirst} ${account.nameLast}` : '')
);
export const selectAccountRole = createSelector(
  selectAccount,
  account => (account ? account.role : null)
);

/**
 * SAGAS
 */
function* fetchAccount({ payload }) {
  const resp = yield call(accountApi.fetchAccount, payload.accountId);

  if (resp.ok) {
    yield put(fetchAccountActions.success(resp.data));
  } else {
    yield put(fetchAccountActions.failure(resp.error));
  }
}

function* fetchAccountDocuments({ payload }) {
  const resp = yield call(accountApi.fetchAccountDocuments, payload);

  if (resp.ok) {
    yield put(fetchAccountDocumentsActions.success(resp.data));
  } else {
    yield put(fetchAccountDocumentsActions.failure(resp.error));
  }
}

function* fetchAccountContracts({ payload }) {
  const resp = yield call(authApi.fetchAccountContracts, payload);

  if (resp.ok) {
    yield put(fetchAccountContractsActions.success(resp.data));
  } else {
    yield put(fetchAccountContractsActions.failure(resp.error));
  }
}

function* updateAccountDocuments({ payload }) {
  const resp = yield call(
    accountApi.updateAccountDocuments,
    payload.accountId,
    payload.documentsData
  );

  if (resp.ok) {
    yield put(updateAccountDocumentsActions.success(resp.data));
    yield put(resetUploadedFilesAction(payload.uploadedFilesId));
  } else {
    yield put(updateAccountDocumentsActions.failure(resp.error));
  }
}

function* acceptAccountDocuments({ payload }) {
  const resp = yield call(accountApi.acceptAccountDocuments, payload.accountId);

  if (resp.ok) {
    yield put(acceptAccountDocumentsActions.success(resp.data));
    yield put(hideModal(accountApiCallIds.ACCEPT_ACCOUNT_DOCUMENTS));
  } else {
    yield put(acceptAccountDocumentsActions.failure(resp.error));
  }
}

function* rejectAccountDocuments({ payload }) {
  const { accountId, rejectReason } = payload;
  const resp = yield call(accountApi.rejectAccountDocuments, accountId, {
    rejectReason,
  });

  if (resp.ok) {
    yield put(rejectAccountDocumentsActions.success(resp.data));
    yield put(hideModal(accountApiCallIds.REJECT_ACCOUNT_DOCUMENTS));
  } else {
    yield put(rejectAccountDocumentsActions.failure(resp.error));
  }
}

function* fetchAccounts({ payload }) {
  const resp = yield call(accountApi.fetchAccounts, payload);

  if (resp.ok) {
    yield put(fetchAccountsActions.success(resp.data));
    yield put(setAccountsPaginationAction(resp.data));
  } else {
    yield put(fetchAccountsActions.failure(resp.error));
  }
}

function* impersonateUser({payload}) {
  const resp = yield call(accountApi.impersonateUser, payload.adminId, payload.userId);

  if (resp.ok) {
    localStorage.setItem('time', moment().toISOString());
    
    if (resp.data.redirectUrl) {
      window.location.href = resp.data.redirectUrl;
    }
  } else {
    yield put(impersonateUserActions.failure(resp.error));
  }
}

function* updateAccount({ payload }) {
  const response = yield call(accountApi.updateAccount, payload.accountId, payload.accountData);

  if (response.ok) {
    yield put(updateAccountActions.success(response.data));
    yield put(setDetailEditableAction(''));
  } else {
    yield put(updateAccountActions.failure(response.error));
  }
}

function* updateAccountProfile({ payload }) {
  const response = yield call(
    accountApi.updateAccountProfile,
    payload.accountId,
    payload.profileData,
    payload.employersData,
    payload.loanObligations
  );

  if (response.ok) {
    yield put(updateAccountProfileActions.success(response.data));
    yield put(setProfileEditableAction(''));
  } else {
    yield put(updateAccountProfileActions.failure(response.error));
  }
}

function* fetchAccountLoans({ payload }) {
  const resp = yield call(loanApi.fetchLoans, payload);

  if (resp.ok) {
    yield put(fetchAccountLoansActions.success(resp.data));
    yield put(setAccountLoansPaginationAction(resp.data));
  } else {
    yield put(fetchAccountLoansActions.failure(resp.error));
  }
}

function* fetchAccountActiveLoans({ payload }) {
  const resp = yield call(loanApi.fetchActiveLoans, payload);

  if (resp.ok) {
    yield put(fetchAccountActiveLoansActions.success(resp.data));
  } else {
    yield put(fetchAccountActiveLoansActions.failure(resp.error));
  }
}

function* fetchAccountAllocations({ payload }) {
  const resp = yield call(allocationApi.fetchAllocations, payload);

  if (resp.ok) {
    yield put(fetchAccountAllocationsActions.success(resp.data));
    yield put(setAccountAllocationsPaginationAction(resp.data));
  } else {
    yield put(fetchAccountAllocationsActions.failure(resp.error));
  }
}

function* createAdministrator({ payload }) {
  const response = yield call(accountApi.createAdministrator, payload.accountData);

  if (response.ok) {
    yield put(createAdministratorActions.success(response.data));
    yield put(push(`/admin/accounts/${response.data.id}`));
  } else {
    yield put(createAdministratorActions.failure(response.error));
  }
}

function* fetchAccountMessages({ payload }) {
  const resp = yield call(accountApi.fetchAccountMessages, payload.accountId, payload.options);

  if (resp.ok) {
    yield put(fetchAccountMessagesActions.success(resp.data));
  }
}

function* fetchAccountAllMessages({ payload }) {
  const resp = yield call(accountApi.fetchAccountAllMessages, payload.accountId);

  if (resp.ok) {
    yield put(fetchAccountAllMessagesActions.success(resp.data));
  }
}

function* createAccountMessage({ payload }) {
  const response = yield call(accountApi.createAccountMessage, payload.accountId, payload.message);

  if (response.ok) {
    yield put(createAccountMessageActions.success({ data: response.data, messagesFilter: payload.messagesFilter }));
  }
}

function* fetchIdCardInfo({ payload }) {
  const response = yield call(accountApi.fetchIdCardInfo, payload.accountId);

  if (response.ok) {
    yield put(fetchIdCardInfoActions.success(response.data));
  } else {
    yield put(fetchIdCardInfoActions.failure(response.error));
  }
}

function* fetchAccountUpdateLogs({ payload }) {
  const response = yield call(accountApi.fetchAccountUpdateLogs, payload.accountId);

  if (response.ok) {
    yield put(fetchAccountUpdateLogsActions.success({ data: response.data }));
  } else {
    yield put(fetchIdCardInfoActions.failure(response.error));
  }
}

function* fetchFeeAccountAmount() {
  const resp = yield call(accountApi.fetchFeeAccountAmount)

  if (resp.ok){
    yield put(fetchSystemFeeAccountAmountActions.success(resp.data))
  }
  else {
    yield put (fetchSystemFeeAccountAmountActions.failure(resp.error))
  }
}

function* checkGeneralContractUptoDate({ payload }) {
  const resp = yield call(accountApi.checkGeneralContractDate, payload.accountId)

  if(resp.ok) {
    yield put(checkGeneralContractDateActions.success(resp.data))
    if(resp.data === true){
      // yield put(fetchAccountActions.request(payload))
      // yield put(push('/dashboard/loans'))
    }
  }
  else {
    yield put(checkGeneralContractDateActions.failure(resp.error))
  } 
}

export function* accountSaga() {
  yield takeLatest(createActionType(FETCH_ACCOUNT, REQUEST), fetchAccount);
  yield takeLatest(createActionType(FETCH_ACCOUNT_DOCUMENTS, REQUEST), fetchAccountDocuments);
  yield takeLatest(createActionType(UPDATE_ACCOUNT_DOCUMENTS, REQUEST), updateAccountDocuments);
  yield takeLatest(createActionType(ACCEPT_ACCOUNT_DOCUMENTS, REQUEST), acceptAccountDocuments);
  yield takeLatest(createActionType(REJECT_ACCOUNT_DOCUMENTS, REQUEST), rejectAccountDocuments);
  yield takeLatest(createActionType(FETCH_ACCOUNT_CONTRACTS, REQUEST), fetchAccountContracts);
  yield takeLatest(createActionType(FETCH_ACCOUNT_LOANS, REQUEST), fetchAccountLoans);
  yield takeLatest(createActionType(FETCH_ACCOUNT_ACTIVE_LOANS, REQUEST), fetchAccountActiveLoans);
  yield takeLatest(createActionType(FETCH_ACCOUNT_ALLOCATIONS, REQUEST), fetchAccountAllocations);
  yield takeLatest(createActionType(FETCH_ACCOUNTS, REQUEST), fetchAccounts);
  yield takeLatest(createActionType(UPDATE_ACCOUNT, REQUEST), updateAccount);
  yield takeLatest(createActionType(IMPERSONATE_USER, REQUEST), impersonateUser);
  yield takeLatest(createActionType(UPDATE_ACCOUNT_PROFILE, REQUEST), updateAccountProfile);
  yield takeLatest(createActionType(CREATE_ADMINISTRATOR, REQUEST), createAdministrator);
  yield takeLatest(createActionType(FETCH_ACCOUNT_MESSAGES, REQUEST), fetchAccountMessages);
  yield takeLatest(createActionType(FETCH_ACCOUNT_ALL_MESSAGES, REQUEST), fetchAccountAllMessages);
  yield takeLatest(createActionType(CREATE_ACCOUNT_MESSAGE, REQUEST), createAccountMessage);
  yield takeLatest(createActionType(FETCH_ID_CARD_INFO, REQUEST), fetchIdCardInfo);
  yield takeLatest(createActionType(FETCH_ACCOUNT_UPDATE_LOGS, REQUEST), fetchAccountUpdateLogs);
  yield takeLatest(SET_ACCOUNTS_FILTER, fetchAccounts);
  yield takeLatest(SET_ACCOUNT_LOANS_FILTER, fetchAccountLoans);
  yield takeLatest(SET_ACCOUNT_ALLOCATIONS_FILTER, fetchAccountAllocations);
  yield takeLatest(SET_ACCOUNT_MESSAGES_FILTER, fetchAccountMessages);
  yield takeLatest(createActionType(FETCH_FEE_ACCOUNT_AMOUNT, REQUEST), fetchFeeAccountAmount);
  yield takeLatest(createActionType(CHECK_GENERAL_CONTRACT_DATE, REQUEST), checkGeneralContractUptoDate);
}



// WEBPACK FOOTER //
// ./src/features/account-management/ducks/index.js